import {
  type AccountInfo,
  InteractionType,
  type PublicClientApplication,
} from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from '@azure/msal-react';
import { faro } from '@grafana/faro-web-sdk';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { type UserToken, useUserStore } from '../stores/user-store';
import { ApiProvider } from './api';
import { AssistantProvider } from './assistant';

interface AuthProviderProps {
  msalInstance: PublicClientApplication;
}

function ActiveAccountProvider({ children }: PropsWithChildren) {
  const { instance, accounts } = useMsal();
  const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(null);
  const setUserType = useUserStore((state) => state.setUserType);

  useEffect(() => {
    if (!activeAccount && accounts.length) {
      const firstAccount = accounts[0];
      if (!firstAccount) return;
      instance.setActiveAccount(firstAccount);
      setActiveAccount(firstAccount);
      const oid = firstAccount.idTokenClaims?.oid;
      if (oid) {
        faro.api.setUser({ id: oid });
      }
      setUserType(firstAccount?.idTokenClaims as UserToken);
    }
  }, [activeAccount, accounts, instance, setUserType]);

  return activeAccount ? children : null;
}

export default function AuthProvider({
  msalInstance,
  children,
}: PropsWithChildren<AuthProviderProps>) {
  if (import.meta.env.MODE !== 'sandbox') {
    return (
      <MsalProvider instance={msalInstance}>
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <ActiveAccountProvider>
            <ApiProvider>
              <AssistantProvider>{children}</AssistantProvider>
            </ApiProvider>
          </ActiveAccountProvider>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    );
  } else {
    return (
      <ApiProvider>
        <AssistantProvider>{children}</AssistantProvider>
      </ApiProvider>
    );
  }
}
